import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { getSystemLoggingStats } from '../../../../../services/SystemLoggingService';
import {
  setStatsError,
  setStatsLoading,
  setStatsSuccess
} from '../../../../../store/slices/systemLoggingSlice';
import { OVERVIEW_CHART_COLORS } from './OverviewChartCard.constant';

ChartJS.register(ArcElement, Tooltip, Legend);

export function OverviewChartCard() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { stats } = useAppSelector((state) => state.systemLogging);

  useEffect(() => {
    const fetchStats = async () => {
      if (!id) return;

      try {
        dispatch(setStatsLoading(true));
        const data = await getSystemLoggingStats(id);
        dispatch(setStatsSuccess(data));
      } catch (error: any) {
        dispatch(setStatsError(error.message || 'Failed to fetch stats'));
      }
    };

    fetchStats();
  }, [id, dispatch]);

  const datasetChanges = stats?.overview.datasetChanges || 0;
  const complianceActions = stats?.overview.complianceActions || 0;
  const totalData = datasetChanges + complianceActions;
  const hasNoData = totalData === 0;

  const data = hasNoData
    ? {
        labels: ['No Data'],
        datasets: [
          {
            data: [100],
            backgroundColor: [OVERVIEW_CHART_COLORS.noData],
            borderWidth: 0,
            cutout: '70%'
          }
        ]
      }
    : {
        labels: ['Dataset Changes', 'Compliance Actions'],
        datasets: [
          {
            data: [datasetChanges, complianceActions],
            backgroundColor: [
              OVERVIEW_CHART_COLORS.datasetChanges,
              OVERVIEW_CHART_COLORS.complianceActions
            ],
            borderWidth: 0,
            cutout: '70%'
          }
        ]
      };

  const options = {
    layout: {
      padding: 12
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: !hasNoData
      }
    },
    maintainAspectRatio: true,
    responsive: true,
    ...(hasNoData && { events: [] })
  };

  return (
    <div className="flex flex-col rounded-lg bg-white py-6 px-4 gap-3 ring-1 ring-[#DADADD] z-[1]">
      <span className="font-inter font-medium text-xs leading-4 tracking-[0.05em] uppercase text-[#767681] text-left">
        OVERVIEW
      </span>
      <div className="flex flex-col items-center gap-3 justify-between flex-1 overflow-hidden">
        <div className="w-[130px] h-[130px] flex-shrink-0 relative z-50">
          <Doughnut data={data} options={options} />
          {hasNoData && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="font-inter font-medium text-sm leading-5 text-center text-[#111827] whitespace-nowrap">
                No data available
              </span>
            </div>
          )}
        </div>
        {hasNoData ? (
          <div className="h-[10px]"></div>
        ) : (
          <div className="flex gap-2 justify-center flex-wrap">
            <div className="flex items-center gap-1">
              <div className="w-6 h-2 bg-purple-300 rounded-sm"></div>
              <span className="font-inter text-[10px] leading-[10px] text-gray-700">
                Dataset Changes
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-2 bg-orange-400 rounded-sm"></div>
              <span className="font-inter text-[10px] leading-[10px] text-gray-700">
                Compliance Actions
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
